import {default as viewportSizesDefault} from "./viewports";
import {Result, ViewportSize} from "./types";
export default class EmbedsSizesGenerator {

    protected controller: AbortController
    protected mainClass: string;
    private results: Array<Result>;
    readonly markup: string;
    readonly viewportSizes: Array<ViewportSize>

    constructor(markup: string, viewportSizes?: Array<ViewportSize>) {
        this.controller = new AbortController();
        this.markup = markup;
        this.viewportSizes = viewportSizes || viewportSizesDefault;
    }

    async run() {

        document.getElementById("calculation").hidden = true;

        return await this.calculate(this.markup);
    }

    async calculate(markup) {
        const calculationSection = document.getElementById("calculation");
        calculationSection.hidden = false;
        calculationSection.scrollIntoView();

        const currentViewportWidthSpan = document.getElementById(
            "current-viewport-width"
        );
        const currentViewportHeightSpan = document.getElementById(
            "current-viewport-height"
        );

        const maxWidth = calculationSection.offsetWidth;

        const progress = document.querySelector("progress");
        progress.max = this.viewportSizes.length;
        let i = 1;
        progress.value = 0;

        const results = [];
        const signal = this.controller.signal;

        for (const viewportSize of this.viewportSizes) {
            progress.value = i;
            currentViewportWidthSpan.textContent = viewportSize.width.toString();
            currentViewportHeightSpan.textContent = viewportSize.height.toString();

            const data = await this.calculateViewportSize({
                maxWidth,
                markup,
                signal,
                ...viewportSize,
            });

            results.push({
                ...data,
                viewportSize,
            });

            i++;
        }
        calculationSection.hidden = true;

        results.sort((a, b) => {
            return b.viewportSize.width - a.viewportSize.width;
        });

        return results;
    }

    async terminate() {
        const terminationSection = document.getElementById("termination");
        terminationSection.hidden = false;

        // Make sure the results are sorted by viewport width in descending order.
        this.results.sort((a, b) => {
            return b.viewportSize.width - a.viewportSize.width;
        });

        const markupViewportSizesOl = document.getElementById(
            "markup-viewport-sizes"
        );
        while (markupViewportSizesOl.firstChild) {
            markupViewportSizesOl.removeChild(markupViewportSizesOl.firstChild);
        }
        for (const result of this.results) {
            const li = document.createElement("li");
            li.textContent = JSON.stringify(result);
            markupViewportSizesOl.appendChild(li);
        }

        const mainClass = this.mainClass;
        const containerId = `layout-shift-termination-${Math.random()}`.replace(
            ".",
            "-"
        );

        let styleTag = `<style class='${mainClass}'>`;
        styleTag += `\n@supports (contain: inline-size) {`;
        styleTag += `\n#${containerId} { container-type: inline-size; }`;
        styleTag += `\n#${containerId} > .${mainClass} { min-height:${this.results[0].height}px; }`;
        for (const result of this.results.slice(1)) {
            styleTag += `\n@container ( max-width: ${result.viewportSize.width}px ) { #${containerId} > .layout-shift-terminated { min-height:${result.height}px; } }`;
        }
        styleTag += "\n}";
        styleTag += "\n</style>";

        const terminateLayoutShift = function (root) {
            setTimeout(function () {
                [].map.call(root.querySelectorAll(`".${mainClass}"`), (el) =>
                    el.remove()
                );
            }, 4000);
        };

        let scriptTag = `<script class="${mainClass}" type="application/javascript" async>`;
        scriptTag += `(${terminateLayoutShift.toString()})(document.getElementById(${JSON.stringify(
            containerId
        )}))`;
        scriptTag += `</script>`;


        const optimizedMarkup = `\n${styleTag}\n${scriptTag}\n`;

        console.log(optimizedMarkup);
        return optimizedMarkup;

    }

    async calculateViewportSize({maxWidth, width, height, markup, signal}) {

        const oldIframe = document.querySelector("iframe");
        const iframeWrapper = oldIframe.parentNode;

        const iframe = document.createElement("iframe");
        iframe.src = "about:blank";
        iframe.width = width;
        iframe.height = height;
        if (maxWidth >= width) {
            iframe.style.transform = "";
            // @ts-ignore
            iframeWrapper.style.height = "";
        } else {
            iframe.style.transform = `scale(${maxWidth / width})`;
            // @ts-ignore
            iframeWrapper.style.height = `${height * (maxWidth / width)}px`;
        }
        iframeWrapper.replaceChild(iframe, oldIframe);

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width">
              <style>
                /* Add border to prevent margin collapsing. */
                /*body > div { border: solid 1px transparent; }*/
              </style>
            </head>
            <body>
              <div>${markup}</div>
              <hr>
              <script type="application/javascript">
              // import { watchForEmbedLoaded } from "./measurement";
              function watchForEmbedLoaded(container) {
                  const windowLoaded = new Promise((resolve) => {
                    window.addEventListener("load", resolve);
                  });
                
                  const startTime = new Date();
                
                  return new Promise((resolve, reject) => {
                    const resolveWithResolution = () => {
                      resolve({
                        duration: new Date().valueOf() - startTime.valueOf(),
                        width: container.offsetWidth,
                        height: container.offsetHeight,
                      });
                    };
                
                    // Allow up to 5 seconds to size the embed.
                    setTimeout(resolveWithResolution, 5000);
                
                    // If there is no JS script in the embed, resolve once window loaded.
                    // This allows us to obtain dimensions for an image/video that lacks
                    // width/height attributes.
                    const script = container.querySelector(
                      'script:not([type]), script[type="module"], script[type~="javascript"], *[onload]'
                    );
                    if (!script) {
                      const dimensionLessVideo = container.querySelector(
                        "video:not([width][height])"
                      );
                      if (dimensionLessVideo) {
                        if (dimensionLessVideo.videoWidth && dimensionLessVideo.videoHeight) {
                          resolveWithResolution();
                        } else {
                          dimensionLessVideo.addEventListener(
                            "loadedmetadata",
                            resolveWithResolution
                          );
                        }
                      } else {
                        windowLoaded.then(resolveWithResolution);
                      }
                      return;
                    }
              
                    // Start listening for DOM changes, and stop once a 2.5-second pause is encountered.
                    let resolveTimeoutId = 0;
                    const mutationObserver = new MutationObserver(() => {
                      clearTimeout(resolveTimeoutId);
                
                      const complete = () => {
                        mutationObserver.disconnect();
                        resolveWithResolution();
                      };
                
                      resolveTimeoutId = setTimeout(complete, 2500);
                    });
                    mutationObserver.observe(container, {
                      subtree: true,
                      childList: true,
                      attributes: true,
                    });
                  });
                }
              (async () => {
                const data = await watchForEmbedLoaded(document.querySelector('div'));
                parent.postMessage(data);
              })();
              </script>
            </body>
            </html>
          `;

        if (signal.aborted) {
            return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        return new Promise((resolve, reject) => {
            signal.addEventListener('abort', () => {
                // window.clearTimeout(reject);
                reject(new DOMException('Aborted', 'AbortError'));
            });
            window.addEventListener(
                "message",
                (event) => {
                    if (event.source === iframe.contentWindow) {
                        resolve(event.data);
                    }
                },
                {once: true}
            );
            iframe.contentWindow.document.open();
            iframe.contentWindow.document.write(html);
            iframe.contentWindow.document.close();

            setTimeout(reject, 10000);
        });
    }

    abort(): void {

        this.controller.abort();
        const progress = document.querySelector("progress");
        progress.value = 0;

        const calculationSection = document.getElementById("calculation");
        calculationSection.hidden = true;
    }

}


// WEBPACK FOOTER //
// ./js/utils/embeds/EmbedsSizesGenerator.ts